import React, { Suspense, useEffect, useRef, useState } from 'react';
import { rem } from '../../styles/rem';
import { DeclarationActionTypes } from '../../view-models/DeclarationActionTypes';
import { buildDialogUrl } from '../../utils/dialogFlowUrl';
import useClientHydrated from '../../hooks/useClientHydrated';
import { useDictionary } from '../../hooks/useDictionary';
import DeclarationDrawerHeader from './DeclarationDrawerHeader';
import { DeclarationDrawerContentProps } from './declarationDrawerTypes';
import { getConfirmAction, getDeclarationsToConfirm } from './statusBarHelpers';
import {
  UnavailableAction,
  UnavailableActionModal,
} from './GuardedDialogAction';

const DeclarationDrawerOverlay = !process.env.SERVER
  ? process.env.NODE_ENV === 'test'
    ? require('./DeclarationDrawerOverlay').default
    : React.lazy(() => import('./DeclarationDrawerOverlay'))
  : null;

function DeclarationDrawer({
  declarations,
  dialogPageUrl,
  partyDetailsUrl,
  partyName,
  statusBarModel,
}: DeclarationDrawerContentProps) {
  const { t } = useDictionary();
  const isHydrated = useClientHydrated();
  const [isDrawerVisible, setDrawerVisible] = useState(false);
  const [isExpanded, setExpanded] = useState(false);
  const [unavailableAction, setUnavailableAction] = useState<
    UnavailableAction | undefined
  >();
  const [collapsedHeaderHeight, setCollapsedHeaderHeight] = useState<
    number | null
  >(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerMeasureRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusAfterCloseRef = useRef(false);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);

  const openDrawer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    setDrawerVisible(true);
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setExpanded(true));
    } else {
      setExpanded(true);
    }
  };

  const closeDrawer = () => {
    setExpanded(false);
    restoreFocusAfterCloseRef.current = true;
    closeTimerRef.current = setTimeout(() => {
      setDrawerVisible(false);
      closeTimerRef.current = null;
    }, 240);
  };

  const prioritizeUnavailableAction = (action: UnavailableAction) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setExpanded(false);
    setDrawerVisible(false);
    setUnavailableAction(action);
  };

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isDrawerVisible || !isExpanded) return;

    toggleButtonRef.current?.focus();
  }, [isDrawerVisible, isExpanded]);

  useEffect(() => {
    if (isDrawerVisible || !restoreFocusAfterCloseRef.current) return;

    toggleButtonRef.current?.focus();
    restoreFocusAfterCloseRef.current = false;
  }, [isDrawerVisible]);

  const hasDeclarations = declarations.length > 0;

  useEffect(() => {
    if (!hasDeclarations || typeof document === 'undefined') return undefined;

    document.documentElement.setAttribute('data-declaration-drawer', 'true');

    return () => {
      document.documentElement.removeAttribute('data-declaration-drawer');
    };
  }, [hasDeclarations]);

  const drawerTitle =
    declarations.length > 1
      ? t('myDeclaration.title_plural')
      : t('myDeclaration.title');
  const confirmAction = getConfirmAction(declarations);
  const confirmTargets = getDeclarationsToConfirm(declarations).map(
    (declaration) => ({
      electionType: declaration.electionType,
      partyId: declaration.partyId,
    }),
  );
  const confirmUrl = buildDialogUrl(dialogPageUrl, {
    type: DeclarationActionTypes.confirm,
    electionType: confirmAction.electionType,
    party: confirmAction.partyIds,
  });
  const mobileCollapsedDrawerHeight = rem(168);
  const compactCollapsedDrawerHeight = rem(84);
  const measuredCollapsedDrawerHeight =
    collapsedHeaderHeight !== null ? `${collapsedHeaderHeight}px` : undefined;
  const resolvedMobileCollapsedDrawerHeight =
    measuredCollapsedDrawerHeight ?? mobileCollapsedDrawerHeight;
  const resolvedCompactCollapsedDrawerHeight =
    measuredCollapsedDrawerHeight ?? compactCollapsedDrawerHeight;
  const declarationPartyNames = Array.from(
    new Set(
      declarations
        .map((declaration) => declaration.partyName)
        .filter((name): name is string => !!name),
    ),
  );
  const drawerSummaryPartyName =
    declarationPartyNames.length === 0
      ? partyName
      : declarationPartyNames.length === 1
      ? declarationPartyNames[0]
      : undefined;
  const mobileCollapsedTransform = `translateY(calc(100% - ${resolvedMobileCollapsedDrawerHeight}))`;
  const compactCollapsedTransform = `translateY(calc(100% - ${resolvedCompactCollapsedDrawerHeight}))`;
  const drawerHeader = (
    <DeclarationDrawerHeader
      isOpen={isExpanded}
      onToggle={isExpanded ? closeDrawer : openDrawer}
      partyName={drawerSummaryPartyName}
      title={drawerTitle}
      confirmUrl={confirmUrl}
      hasConfirmCta={confirmAction.hasConfirmCta}
      statusBarModel={statusBarModel}
      toggleButtonRef={toggleButtonRef}
      confirmTargets={confirmTargets}
      declarations={declarations}
      onUnavailableAction={prioritizeUnavailableAction}
    />
  );
  const measuredDrawerHeader = <div ref={headerMeasureRef}>{drawerHeader}</div>;
  const shouldRenderOverlay = isHydrated || isDrawerVisible;

  useEffect(() => {
    const headerElement = headerMeasureRef.current;
    if (!headerElement) return undefined;

    const updateHeaderHeight = () => {
      const nextHeight = Math.ceil(
        headerElement.getBoundingClientRect().height,
      );

      if (nextHeight > 0) {
        setCollapsedHeaderHeight(nextHeight);
      }
    };

    updateHeaderHeight();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(headerElement);

    return () => resizeObserver.disconnect();
  }, [confirmAction.hasConfirmCta, isDrawerVisible, partyName]);

  const collapsedDrawerHeader = !isDrawerVisible ? (
    <div
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 3,
        filter: 'drop-shadow(0 0 7.5px rgba(39, 32, 2, 0.2))',
      }}
    >
      {measuredDrawerHeader}
    </div>
  ) : null;

  if (!hasDeclarations) return null;

  if (unavailableAction) {
    return (
      <UnavailableActionModal
        action={unavailableAction}
        onDismiss={() => setUnavailableAction(undefined)}
      />
    );
  }

  if (!shouldRenderOverlay) {
    return collapsedDrawerHeader;
  }

  if (DeclarationDrawerOverlay === null) return collapsedDrawerHeader;

  if (process.env.NODE_ENV === 'test') {
    const TestDeclarationDrawerOverlay = DeclarationDrawerOverlay as React.ComponentType<
      React.ComponentProps<typeof import('./DeclarationDrawerOverlay').default>
    >;

    return (
      <>
        {collapsedDrawerHeader}
        <TestDeclarationDrawerOverlay
          compactCollapsedTransform={compactCollapsedTransform}
          declarations={declarations}
          dialogPageUrl={dialogPageUrl}
          drawerHeader={measuredDrawerHeader}
          drawerTitle={drawerTitle}
          isDrawerVisible={isDrawerVisible}
          isExpanded={isExpanded}
          mobileCollapsedTransform={mobileCollapsedTransform}
          onUnavailableAction={prioritizeUnavailableAction}
          onOpenChange={(open) => {
            if (open) {
              openDrawer();
            } else {
              closeDrawer();
            }
          }}
          partyDetailsUrl={partyDetailsUrl}
          partyName={partyName}
          statusBarModel={statusBarModel}
        />
      </>
    );
  }

  return (
    <>
      {collapsedDrawerHeader}
      <Suspense fallback={null}>
        <DeclarationDrawerOverlay
          compactCollapsedTransform={compactCollapsedTransform}
          declarations={declarations}
          dialogPageUrl={dialogPageUrl}
          drawerHeader={measuredDrawerHeader}
          drawerTitle={drawerTitle}
          isDrawerVisible={isDrawerVisible}
          isExpanded={isExpanded}
          mobileCollapsedTransform={mobileCollapsedTransform}
          onUnavailableAction={prioritizeUnavailableAction}
          onOpenChange={(open) => {
            if (open) {
              openDrawer();
            } else {
              closeDrawer();
            }
          }}
          partyDetailsUrl={partyDetailsUrl}
          partyName={partyName}
          statusBarModel={statusBarModel}
        />
      </Suspense>
    </>
  );
}

export default DeclarationDrawer;
